import { memo } from 'react';
import { AlertCircle, Clock, Edit, Euro, Plus, User, Users } from 'lucide-react';
import type { MapaMesa, Reserva } from '../../types';
import { formatHora, getEstadoClass, getEstadoLabel } from '../../utils/mesaEstado';

interface MesaCardProps {
  mesa: MapaMesa;
  onClick: () => void;
  onNovaReserva: (mesaId: number) => void;
  onOcupar: (mesaId: number) => void;
  onEditar: (r: Reserva) => void;
}

export const MesaCard = memo(function MesaCard({ mesa, onClick, onNovaReserva, onOcupar, onEditar }: MesaCardProps) {
  const reservasList = mesa.reservasAtivas ?? (mesa.reservaAtiva ? [mesa.reservaAtiva] : []);
  const reserva = mesa.reservaAtiva;
  const historico = mesa.ultimoHistorico;
  const isDisabled = !mesa.podeReservar;
  const temHistoricoAberto = !!historico;

  return (
    <article
      className={`mesa-card ${getEstadoClass(mesa.estado)} ${isDisabled ? 'mesa-disabled' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Mesa ${mesa.numero}, ${getEstadoLabel(mesa.estado)}`}
      title={isDisabled ? 'Mesa desabilitada (fora do intervalo)' : undefined}
    >
      <div className="mesa-header">
        <span className="mesa-numero">Mesa {mesa.numero}</span>
        <span className={`mesa-badge ${getEstadoClass(mesa.estado)}`}>
          {isDisabled ? 'Desativada' : getEstadoLabel(mesa.estado)}
        </span>
      </div>

      <div className="mesa-info">
        <div className="mesa-capacidade">
          <Users className="w-4 h-4" aria-hidden="true" />
          <span>
            {mesa.capacidadePessoas} pax — {mesa.descricao}
          </span>
        </div>

        {reservasList.length > 0 ? (
          <div className="reserva-list">
            {reservasList.map((r) => (
              <div
                key={r.id}
                className="reserva-row"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditar(r);
                }}
              >
                <span className="reserva-row-main">
                  <Clock className="w-3 h-3" />
                  {formatHora(r.dataDaReserva)} • {r.numeroPessoas} pax — {r.nomeCliente || `Cliente #${r.clienteId}`}{' '}
                  {r.valor ? `· €${Number(r.valor).toFixed(2)}` : ''}
                </span>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditar(r);
                  }}
                  aria-label="Editar"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : reserva ? (
          <div className="reserva-info">
            <div className="reserva-cliente">
              <User className="w-4 h-4" aria-hidden="true" />
              <span>
                {reserva.nomeCliente || `Cliente #${reserva.clienteId}`} — {reserva.numeroPessoas} pax{' '}
                {reserva.valor ? `· €${Number(reserva.valor).toFixed(2)}` : ''}
              </span>
            </div>
            <div className="reserva-hora">
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span>
                {formatHora(reserva.dataDaReserva)} • {reserva.numeroPessoas} pessoas{' '}
                {reserva.criadoPorNome ? `· Criado por: ${reserva.criadoPorNome}` : ''}{' '}
                {reserva.editadoPorNome ? `· Editado por: ${reserva.editadoPorNome}` : ''}
              </span>
            </div>
            {reserva.notasReserva && <div className="reserva-notas">{reserva.notasReserva}</div>}
          </div>
        ) : historico ? (
          <div className="historico-info">
            <div className="reserva-cliente">
              <User className="w-4 h-4" aria-hidden="true" />
              <span>
                {historico.cliente?.nome || historico.contatoReserva || 'Cliente'} — {historico.numeroPessoas} pax{' '}
                {historico.valor ? `· €${Number(historico.valor).toFixed(2)}` : ''}
              </span>
            </div>
            <div className="reserva-hora">
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span>
                {formatHora(historico.dataMesa)} • {historico.numeroPessoas} pessoas
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {mesa.estado === 3 && (
        <div className="chega-agora-alert">
          <AlertCircle className="w-4 h-4" aria-hidden="true" />
          <span>Chega agora</span>
        </div>
      )}

      {!isDisabled && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          {!temHistoricoAberto && (
            <>
              <button
                className="btn btn-secondary btn-sm w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onNovaReserva(mesa.id);
                }}
              >
                <Plus className="w-4 h-4" />
                Nova reserva
              </button>
              {mesa.estado === 0 && (
                <button
                  className="btn btn-primary btn-sm w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOcupar(mesa.id);
                  }}
                  title="Ocupar sem reserva (walk-in)"
                >
                  <Euro className="w-4 h-4" />
                  Ocupar
                </button>
              )}
            </>
          )}
          {temHistoricoAberto && (
            <button
              className="btn btn-ghost btn-sm w-full"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <Edit className="w-4 h-4" />
              Ver ocupação
            </button>
          )}
        </div>
      )}
    </article>
  );
});
